import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, ArrowRight, Zap, Phone } from 'lucide-react';
import { ComparisonTable } from '../components/pricing/ComparisonTable';
import { FaqSection } from '../components/pricing/FaqSection';

const PLANS = [
  {
    name: 'Basic',
    price: '$69.99',
    period: '/user/month',
    description: 'Essential monitoring and help desk support for growing teams.',
    popular: false,
    features: [
      '24/7 network monitoring',
      'Help desk support (business hours)',
      'Basic endpoint protection',
      'Monthly health reports',
      'Email support',
      '4-hour response SLA',
    ],
    notIncluded: [
      'Advanced cybersecurity',
      'Patch management',
      'Backup services',
      'On-site support',
    ],
    cta: 'Get Started',
    minUsers: '20 user minimum',
  },
  {
    name: 'Standard',
    price: '$89',
    period: '/user/month',
    description: 'Complete protection with cybersecurity and proactive management.',
    popular: true,
    features: [
      'Everything in Basic, plus:',
      'Advanced endpoint protection (EDR)',
      'Email security & phishing defense',
      'Automated patch management',
      'Extended help desk (7am-9pm)',
      '1-hour response SLA',
      'Quarterly business reviews',
      'Security awareness training',
    ],
    notIncluded: [
      'Full backup solution',
      'Strategic consulting',
      'Unlimited on-site visits',
    ],
    cta: 'Most Popular',
    minUsers: '5 user minimum',
  },
  {
    name: 'Premium',
    price: '$129',
    period: '/user/month',
    description: 'Full managed services with backup, consulting, and priority support.',
    popular: false,
    features: [
      'Everything in Standard, plus:',
      'Cloud backup & disaster recovery',
      '24/7 help desk support',
      '15-minute response SLA',
      'Strategic IT consulting',
      'vCIO services',
      'Compliance support',
      'Unlimited remote support',
      '2 on-site visits/month included',
    ],
    notIncluded: [],
    cta: 'Contact Sales',
    minUsers: '10 user minimum',
  },
];

const ADDONS = [
  { name: 'Professional Website Design', price: '$550', unit: 'flat rate' },
  { name: 'Structured Cabling', price: 'Custom', unit: 'quote' },
  { name: 'Additional On-Site Visit', price: '$150', unit: '/visit' },
  { name: 'Cloud Migration Project', price: 'Custom', unit: 'quote' },
  { name: 'Server Management', price: '$200', unit: '/server/mo' },
  { name: 'Mobile Device Management', price: '$8', unit: '/device/mo' },
  { name: 'Advanced Security (MDR)', price: '$25', unit: '/user/mo' },
  { name: 'Microsoft 365 License', price: 'At cost', unit: '+ $5 mgmt' },
];

function PricingCard({ plan }) {
  return (
    <Card
      className={`relative ${
        plan.popular
          ? 'pricing-highlight border-2 border-blue-500 shadow-2xl shadow-blue-500/20'
          : 'border border-slate-200'
      }`}
      data-testid={`pricing-card-${plan.name.toLowerCase()}`}
    >
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full">
            Most Popular
          </span>
        </div>
      )}
      <CardHeader className="text-center pb-0">
        <CardTitle className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {plan.name}
        </CardTitle>
        <div className="mt-4">
          <span className="text-5xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {plan.price}
          </span>
          <span className="text-slate-500">{plan.period}</span>
        </div>
        <p className="text-sm text-slate-600 mt-2">{plan.description}</p>
        <p className="text-xs text-blue-600 font-medium mt-1">{plan.minUsers}</p>
      </CardHeader>
      <CardContent className="pt-6">
        <ul className="space-y-3 mb-8">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-slate-700">{feature}</span>
            </li>
          ))}
          {plan.notIncluded.map((feature) => (
            <li key={feature} className="flex items-start gap-2 opacity-50">
              <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-slate-500 line-through">{feature}</span>
            </li>
          ))}
        </ul>
        <Link to="/contact">
          <Button
            className={`w-full font-semibold ${
              plan.popular
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
            }`}
            data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
          >
            {plan.cta}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default function PricingPage() {
  return (
    <div className="min-h-screen pt-16" data-testid="pricing-page">
      {/* Hero */}
      <section className="bg-slate-950 py-20" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-300">Transparent Pricing</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Simple, Predictable <span className="text-gradient">IT Pricing</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            No hidden fees, no surprise bills. Choose the plan that fits your business
            and know exactly what you'll pay each month.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white" data-testid="pricing-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {PLANS.map((plan) => (
              <PricingCard key={plan.name} plan={plan} />
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-20 bg-slate-50" data-testid="addons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Optional Add-Ons
            </h2>
            <p className="text-lg text-slate-600">Customize your plan with additional services as needed.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {ADDONS.map((addon) => (
              <Card key={addon.name} className="border border-slate-200 bg-white" data-testid={`addon-${addon.name.toLowerCase().replace(/\s/g, '-')}`}>
                <CardContent className="p-4 flex justify-between items-center">
                  <span className="font-medium text-slate-900">{addon.name}</span>
                  <span className="text-blue-600 font-bold">
                    {addon.price}<span className="text-xs text-slate-500 font-normal">{addon.unit}</span>
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <ComparisonTable />
      <FaqSection />

      {/* CTA */}
      <section className="py-20 bg-slate-950" data-testid="pricing-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Get a custom quote based on your specific needs. No obligation, no pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/30" data-testid="pricing-bottom-cta">
                Get Your Custom Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:6026491314">
              <Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10">
                <Phone className="w-4 h-4 mr-2" /> (602) 649-1314
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
